/**
 * Region Distribution component for visualizing sandbox distribution across Azure regions.
 * Uses a clean card-based layout grouped by geographic area.
 *
 * @module components/ui/RegionMap
 */

import { useMemo } from 'react';
import { Globe, Server } from 'lucide-react';

/**
 * Azure region metadata
 */
const REGION_INFO: Record<string, { name: string; geo: 'americas' | 'europe' | 'apac' }> = {
  // Americas
  canadacentral: { name: 'Canada Central', geo: 'americas' },
  canadaeast: { name: 'Canada East', geo: 'americas' },
  centralus: { name: 'Central US', geo: 'americas' },
  eastus2: { name: 'East US 2', geo: 'americas' },
  eastus2euap: { name: 'East US 2 EUAP', geo: 'americas' },
  mexicocentral: { name: 'Mexico Central', geo: 'americas' },
  northcentralus: { name: 'North Central US', geo: 'americas' },
  westcentralus: { name: 'West Central US', geo: 'americas' },
  westus2: { name: 'West US 2', geo: 'americas' },
  // Europe
  germanywestcentral: { name: 'Germany West Central', geo: 'europe' },
  northeurope: { name: 'North Europe', geo: 'europe' },
  swedencentral: { name: 'Sweden Central', geo: 'europe' },
  uksouth: { name: 'UK South', geo: 'europe' },
  // APAC
  eastasia: { name: 'East Asia', geo: 'apac' },
  australiaeast: { name: 'Australia East', geo: 'apac' },
  japanwest: { name: 'Japan West', geo: 'apac' },
};

const GEO_DISPLAY: Record<string, { name: string; color: string; bgColor: string }> = {
  americas: {
    name: 'Americas',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
  },
  europe: {
    name: 'Europe',
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
  },
  apac: {
    name: 'Asia Pacific',
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-50 dark:bg-purple-900/20',
  },
};

interface RegionMapProps {
  /** Map of region name to sandbox count */
  regionCounts: Record<string, number>;
  /** Optional CSS class */
  className?: string;
}

/**
 * A clean visualization showing sandbox counts per Azure region, grouped by geography
 */
export function RegionMap({ regionCounts, className = '' }: RegionMapProps) {
  const { groupedRegions, totalCount, maxCount } = useMemo(() => {
    const grouped: Record<string, Array<{ region: string; name: string; count: number }>> = {
      americas: [],
      europe: [],
      apac: [],
    };
    let totalCount = 0;
    let maxCount = 0;

    for (const [region, count] of Object.entries(regionCounts)) {
      const info = REGION_INFO[region.toLowerCase()];
      if (info && count > 0) {
        grouped[info.geo].push({ region, name: info.name, count });
        totalCount += count;
        maxCount = Math.max(maxCount, count);
      }
    }

    // Sort each group by count descending
    for (const geo of Object.keys(grouped)) {
      grouped[geo].sort((a, b) => b.count - a.count);
    }

    return { groupedRegions: grouped, totalCount, maxCount };
  }, [regionCounts]);

  if (totalCount === 0) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <Globe className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" />
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          No sandboxes in this group yet
        </p>
      </div>
    );
  }

  const activeGeos = Object.entries(groupedRegions).filter(([, regions]) => regions.length > 0);

  return (
    <div className={className}>
      {/* Region cards by geography */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {activeGeos.map(([geo, regions]) => {
          const geoInfo = GEO_DISPLAY[geo];
          const geoTotal = regions.reduce((sum, r) => sum + r.count, 0);

          return (
            <div
              key={geo}
              className={`rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden`}
            >
              {/* Geo header */}
              <div
                className={`px-4 py-3 ${geoInfo.bgColor} border-b border-gray-200 dark:border-gray-700`}
              >
                <div className="flex items-center justify-between">
                  <span className={`font-medium ${geoInfo.color}`}>{geoInfo.name}</span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {geoTotal} sandbox{geoTotal !== 1 ? 'es' : ''}
                  </span>
                </div>
              </div>

              {/* Regions list */}
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {regions.map((region) => {
                  const percentage = maxCount > 0 ? (region.count / maxCount) * 100 : 0;

                  return (
                    <div key={region.region} className="px-4 py-3 bg-white dark:bg-[#0a0a0a]">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <Server className="h-3.5 w-3.5 text-gray-400" />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {region.name}
                          </span>
                        </div>
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {region.count}
                        </span>
                      </div>
                      {/* Progress bar */}
                      <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary-500 rounded-full transition-all duration-300"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
        <span className="font-medium text-gray-900 dark:text-gray-100">{totalCount}</span> sandbox
        {totalCount !== 1 ? 'es' : ''} across{' '}
        <span className="font-medium text-gray-900 dark:text-gray-100">
          {Object.values(groupedRegions).flat().length}
        </span>{' '}
        region{Object.values(groupedRegions).flat().length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}
